import { useRef, useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Check, CheckCheck, Paperclip, X, FileText, Download } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, isToday, isYesterday, isSameDay } from "date-fns";
import { useUploadAttachment, type Attachment } from "@/hooks/useTeamComms";
import type { TeamMessage } from "@/hooks/useTeamComms";
import { toast } from "sonner";

interface MessageThreadProps {
  messages: TeamMessage[];
  currentUserId: string;
  onSend: (body: string, attachments?: Attachment[]) => void;
  onMarkRead: (messageIds: string[]) => void;
  isLoading?: boolean;
  conversationMembers?: { user_id: string; first_name: string; last_name: string }[];
  conversationId?: string;
}

const formatDateHeader = (date: Date) => {
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "EEEE, MMMM d, yyyy");
};

const isImage = (type: string) => type.startsWith("image/");

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AttachmentPreview = ({ attachment, isMine }: { attachment: Attachment; isMine: boolean }) => {
  if (isImage(attachment.type)) {
    return (
      <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="block mt-1">
        <img
          src={attachment.url}
          alt={attachment.name}
          className="rounded-lg max-w-[240px] max-h-[200px] object-cover cursor-pointer hover:opacity-90 transition-opacity"
        />
      </a>
    );
  }
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "flex items-center gap-2 mt-1 px-3 py-2 rounded-lg text-xs hover:opacity-80 transition-opacity",
        isMine ? "bg-primary-foreground/10" : "bg-background/60"
      )}
    >
      <FileText className="h-4 w-4 shrink-0" />
      <span className="truncate max-w-[160px]">{attachment.name}</span>
      <span className="text-[10px] opacity-60 shrink-0">{formatFileSize(attachment.size)}</span>
      <Download className="h-3 w-3 shrink-0 ml-auto" />
    </a>
  );
};

export const MessageThread = ({ messages, currentUserId, onSend, onMarkRead, isLoading, conversationMembers, conversationId }: MessageThreadProps) => {
  const [input, setInput] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadAttachment = useUploadAttachment();

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // Mark unread messages as read
  useEffect(() => {
    const unreadIds = messages
      .filter(m => m.author_id !== currentUserId && !(m.read_by as Record<string, string>)?.[currentUserId])
      .map(m => m.id);
    if (unreadIds.length > 0) {
      onMarkRead(unreadIds);
    }
  }, [messages, currentUserId, onMarkRead]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed && pendingFiles.length === 0) return;
    if (!conversationId && pendingFiles.length > 0) {
      toast.error("Cannot upload attachments without a conversation");
      return;
    }

    let attachments: Attachment[] = [];
    if (pendingFiles.length > 0 && conversationId) {
      setUploading(true);
      try {
        const results = await Promise.all(
          pendingFiles.map(file => uploadAttachment.mutateAsync({ file, conversationId }))
        );
        attachments = results;
      } catch (err: any) {
        toast.error(err.message || "Failed to upload attachment");
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    onSend(trimmed || "", attachments.length > 0 ? attachments : undefined);
    setInput("");
    setPendingFiles([]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    // Max 5 files at a time, 20MB each
    const valid = files.filter(f => f.size <= 20 * 1024 * 1024).slice(0, 5);
    if (valid.length < files.length) {
      toast.error("Some files were skipped (max 20MB each, 5 files)");
    }
    setPendingFiles(prev => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePendingFile = (idx: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const getReadStatus = (msg: TeamMessage) => {
    if (msg.author_id !== currentUserId) return null;
    const readBy = msg.read_by ?? {};
    const readers = Object.keys(readBy).filter(id => id !== currentUserId);
    if (readers.length > 0) {
      const readerNames = readers.map(id => {
        const member = conversationMembers?.find(m => m.user_id === id);
        return member ? member.first_name : "Someone";
      });
      return { type: "read" as const, names: readerNames };
    }
    return { type: "delivered" as const, names: [] };
  };

  let lastDate: Date | null = null;

  return (
    <div className="flex flex-col h-full">
      {/* Messages area */}
      <div ref={containerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        )}
        {messages.map((msg, idx) => {
          const isMine = msg.author_id === currentUserId;
          const msgDate = new Date(msg.created_at);
          const showDateHeader = !lastDate || !isSameDay(lastDate, msgDate);
          lastDate = msgDate;
          const readStatus = getReadStatus(msg);
          const prevMsg = idx > 0 ? messages[idx - 1] : null;
          const showAvatar = !isMine && (!prevMsg || prevMsg.author_id !== msg.author_id);
          const msgAttachments: Attachment[] = msg.attachments ?? [];

          return (
            <div key={msg.id}>
              {showDateHeader && (
                <div className="flex justify-center my-3">
                  <span className="text-[11px] text-muted-foreground bg-muted/60 px-3 py-0.5 rounded-full">
                    {formatDateHeader(msgDate)}
                  </span>
                </div>
              )}
              <div className={cn("flex gap-2 mb-0.5", isMine ? "justify-end" : "justify-start")}>
                {!isMine && (
                  <div className="w-8 shrink-0">
                    {showAvatar && (
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={msg.author?.profile_photo_url ?? undefined} />
                        <AvatarFallback className="text-[10px] bg-muted">
                          {msg.author ? `${msg.author.first_name[0]}${msg.author.last_name[0]}` : "?"}
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                )}
                <div className={cn("max-w-[75%]")}>
                  {showAvatar && !isMine && msg.author && (
                    <p className="text-[11px] text-muted-foreground ml-3 mb-0.5">
                      {msg.author.first_name} {msg.author.last_name}
                    </p>
                  )}
                  <div
                    className={cn(
                      "px-3.5 py-2 text-sm leading-relaxed",
                      isMine
                        ? "bg-primary text-primary-foreground rounded-2xl rounded-br-md"
                        : "bg-muted text-foreground rounded-2xl rounded-bl-md"
                    )}
                  >
                    {msg.body && <p>{msg.body}</p>}
                    {msgAttachments.length > 0 && (
                      <div className={cn("space-y-1", msg.body ? "mt-1" : "")}>
                        {msgAttachments.map((att, i) => (
                          <AttachmentPreview key={i} attachment={att} isMine={isMine} />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={cn("flex items-center gap-1 mt-0.5 px-1", isMine ? "justify-end" : "justify-start")}>
                    <span className="text-[10px] text-muted-foreground">
                      {format(msgDate, "h:mm a")}
                    </span>
                    {readStatus && (
                      <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                        {readStatus.type === "read" ? (
                          <>
                            <CheckCheck className="h-3 w-3 text-primary" />
                            <span>Read</span>
                          </>
                        ) : (
                          <>
                            <Check className="h-3 w-3" />
                            <span>Delivered</span>
                          </>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Pending attachments preview */}
      {pendingFiles.length > 0 && (
        <div className="border-t bg-muted/30 px-4 py-2 flex gap-2 flex-wrap">
          {pendingFiles.map((file, i) => (
            <div key={i} className="relative group flex items-center gap-1.5 bg-background border rounded-lg px-2 py-1.5 text-xs">
              {file.type.startsWith("image/") ? (
                <img src={URL.createObjectURL(file)} alt={file.name} className="h-10 w-10 rounded object-cover" />
              ) : (
                <FileText className="h-5 w-5 text-muted-foreground" />
              )}
              <div className="max-w-[100px]">
                <p className="truncate font-medium">{file.name}</p>
                <p className="text-muted-foreground">{formatFileSize(file.size)}</p>
              </div>
              <button
                onClick={() => removePendingFile(i)}
                className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input bar — iMessage-style */}
      <div className="border-t bg-card p-3">
        <div className="flex items-center gap-2 bg-muted/50 rounded-full px-4 py-1.5">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
            onChange={handleFileSelect}
          />
          <Button
            size="icon"
            variant="ghost"
            onClick={() => fileInputRef.current?.click()}
            className="h-8 w-8 rounded-full shrink-0"
            type="button"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleSend()}
            placeholder="iMessage"
            className="border-0 bg-transparent shadow-none focus-visible:ring-0 text-sm h-8 px-0"
          />
          <Button
            size="icon"
            variant="ghost"
            onClick={handleSend}
            disabled={(!input.trim() && pendingFiles.length === 0) || uploading}
            className="h-8 w-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-30"
          >
            {uploading ? (
              <div className="animate-spin h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
